import React, { useEffect, useRef, useState } from 'react';
import { FaSearch } from "react-icons/fa";
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { MdAdd, MdClose } from 'react-icons/md';
import Spinners from '../../../../../../app/common-components/Spinner';
import DiscardChanges from '../../../../../../app/common-components/DiscardChanges';
import CustomModal from '../../../../../../app/common-components/CustomModal';
import Input from '../../../../../../app/common-components/form/input';
import ButtonComponent from '../../../../../../app/common-components/ButtonComponent';
import { sortAlphabetically, validateKeyword } from './DragDropConfigModal.helpers';
import styles from './DragDropConfigModal.module.css';

// Custom Audit File Dropdown with search
function AuditDropdown({ items, value, onChange, placeholder }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = items.filter((item) =>
        item.toLowerCase().includes(search.toLowerCase())
    );

    const handleSelect = (item) => {
        onChange(item);
        setOpen(false);
        setSearch('');
    };

    return (
        <div ref={ref} className={styles.customDropdown}>
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className={`${styles.customDropdownTrigger} ${open ? styles.customDropdownTriggerOpen : ''}`}
            >
                <span className={value ? styles.customDropdownValue : styles.customDropdownPlaceholder}>
                    {value || placeholder}
                </span>
                <span className={`${styles.customDropdownArrow} ${open ? styles.customDropdownArrowOpen : ''}`} />
            </button>

            {open && (
                <div className={styles.customDropdownMenu}>
                    <Input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search"
                        inputClassName={styles.customDropdownSearchInput}
                        containerClassName={styles.customDropdownSearchBox}
                        endIconImage={<FaSearch className={styles.searchIcon} />}
                        endIcon={true}
                        labelClassName=""
                    />
                    <ul className={styles.customDropdownList}>
                        {filtered.length === 0 ? (
                            <li className={styles.customDropdownEmpty}>No files found</li>
                        ) : (
                            filtered.map((item) => (
                                <li
                                    key={item}
                                    onClick={() => handleSelect(item)}
                                    className={`${styles.customDropdownItem} ${item === value ? styles.customDropdownItemActive : ''}`}
                                >
                                    {item}
                                </li>
                            ))
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}

// Draggable Chip Component
function DraggableChip({ name, index, source, removable, onRemove }) {
    return (
        <Draggable draggableId={`${source}-${name}`} index={index}>
            {(provided, snapshot) => (
                <div
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`${styles.pluginChip} ${source === 'available' ? styles.pluginChipAvailable : styles.pluginChipSelected
                        } ${snapshot.isDragging ? styles.pluginChipDragging : ''} ${snapshot.draggingOver === 'selected' ? styles.pluginChipDraggingOverTarget : snapshot.draggingOver === 'available' ? styles.pluginChipDraggingOverSource : ''}`}
                    style={provided.draggableProps.style}
                >
                    <div className={styles.chipContent}>
                        <span>{name}</span>
                    </div>
                    {removable && onRemove && (
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemove();
                            }}
                            className={styles.chipRemoveButton}
                            aria-label={`Remove ${name}`}
                        >
                            <MdClose className={styles.chipRemoveIcon} />
                        </button>
                    )}
                </div>
            )}
        </Draggable>
    );
}

// Drag-Drop Panels Component
function DragDropPanels({
    availableItems,
    selectedItems,
    onRemoveFromSelected,
    labels,
    errors,
    extraField,
}) {
    return (
        <div className={styles.pluginPanelsContainer}>
            {/* Available Items */}
            <div className={styles.pluginPanel}>
                <label className={styles.pluginPanelLabel}>{labels.availableLabel}</label>
                <Droppable droppableId="available">
                    {(provided, snapshot) => (
                        <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`${styles.droppableArea} ${styles.sourceArea} ${snapshot.isDraggingOver && !snapshot.draggingFromThisWith
                                ? styles.droppableAreaDraggingAvailable
                                : ''
                                } ${snapshot.draggingFromThisWith
                                    ? styles.droppableAreaDragSource
                                    : ''}`}
                        >
                            <div className={styles.chipsContainer}>
                                {availableItems?.map((item, index) => (
                                    <DraggableChip
                                        key={item}
                                        name={item}
                                        index={index}
                                        source="available"
                                        removable={false}
                                    />
                                ))}
                                {snapshot.isDraggingOver ? null : provided.placeholder}
                                {availableItems.length === 0 && !snapshot.isDraggingOver && (
                                    <p className={styles.emptyMessage}>{labels.availableEmpty || 'No available items'}</p>
                                )}
                            </div>
                        </div>
                    )}
                </Droppable>
            </div>

            {/* Selected Items + optional extra field */}
            <div className={styles.pluginPanel}>
                <div className={styles.formGroup} style={{ marginBottom: extraField ? '12px' : '0' }}>
                    <div className={styles.labelHeader}>
                        <label className={styles.pluginPanelLabel} style={{ marginBottom: 0 }}>
                            {labels.selectedLabel}<span className={styles.requiredAsterisk}>*</span>
                        </label>
                        {errors.selectedItems && <span className={styles.errorText}>{labels.selectedError || 'At least one item must be selected'}</span>}
                    </div>
                    <Droppable droppableId="selected">
                        {(provided, snapshot) => (
                            <div
                                ref={provided.innerRef}
                                {...provided.droppableProps}
                                className={`${styles.droppableArea} ${styles.targetArea} ${snapshot.isDraggingOver && !snapshot.draggingFromThisWith
                                    ? styles.droppableAreaDraggingSelected
                                    : ''
                                    } ${snapshot.draggingFromThisWith
                                        ? styles.droppableAreaDragSource
                                        : ''} ${errors.selectedItems ? styles.inputError : ''}`}
                                style={extraField ? { minHeight: '135px', maxHeight: '135px' } : undefined}
                            >
                                <div className={styles.chipsContainer}>
                                    {selectedItems?.map((item, index) => (
                                        <DraggableChip
                                            key={item}
                                            name={item}
                                            index={index}
                                            source="selected"
                                            removable={true}
                                            onRemove={() => onRemoveFromSelected(item)}
                                        />
                                    ))}
                                    {snapshot.isDraggingOver ? null : provided.placeholder}
                                    {selectedItems.length === 0 && !snapshot.isDraggingOver && (
                                        <p className={styles.emptyMessage}>{labels.selectedEmpty || 'Drag items here'}</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </Droppable>
                </div>

                {/* Extra field (e.g., Reason for CIS) */}
                {extraField && extraField}
            </div>
        </div>
    );
}

// Keyword Input Component
function KeywordInput({ keywords, onKeywordsChange, showAddIcon, requiresNumericStart, error, onErrorChange }) {
    const [inputValue, setInputValue] = useState('');

    const addKeyword = () => {
        const trimmedValue = inputValue.trim();
        if (!trimmedValue) { return; }

        const validationError = validateKeyword(trimmedValue, requiresNumericStart);
        if (validationError) {
            onErrorChange?.(validationError);
            return;
        }

        if (!(keywords || []).includes(trimmedValue)) {
            onKeywordsChange(sortAlphabetically([...(keywords || []), trimmedValue]));
            setInputValue('');
            onErrorChange?.('');
        }
    };

    const removeKeyword = (keywordToRemove) => {
        onKeywordsChange((keywords || []).filter((k) => k !== keywordToRemove));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addKeyword();
        }
    };

    return (
        <div className={styles.keywordInputContainer}>
            <div className={styles.keywordInputRow}>
                <input
                    type="text"
                    value={inputValue}
                    onChange={(e) => {
                        setInputValue(e.target.value);
                        if (error) {
                            onErrorChange?.('');
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter Rules To Exclude"
                    className={`${styles.keywordTextInput} ${error ? styles.inputError : ''}`}
                />
                <button
                    onClick={addKeyword}
                    disabled={!inputValue.trim()}
                    className={`${styles.addKeywordButton} ${inputValue.trim() ? styles.addKeywordButtonEnabled : styles.addKeywordButtonDisabled
                        }`}
                >
                    {showAddIcon && <MdAdd className={styles.addKeywordIcon} />}
                    Add
                </button>
            </div>

            <div className={styles.keywordChipsContainer}>
                {keywords?.map((keyword) => (
                    <div key={keyword} className={styles.keywordChip}>
                        <span>{keyword}</span>
                        <button
                            onClick={() => removeKeyword(keyword)}
                            className={styles.keywordRemoveButton}
                            aria-label={`Remove ${keyword}`}
                        >
                            <MdClose className={styles.keywordRemoveIcon} />
                        </button>
                    </div>
                ))}
                {keywords.length === 0 && (
                    <p className={styles.keywordEmptyMessage}>No rules added yet</p>
                )}
            </div>
        </div>
    );
}

// Main DragDropConfigModal Component
export default function DragDropConfigModal({
    availableItems = [],
    isOpen,
    onClose,
    onSave,
    initialData,
    isLoading,
    labels = {},
    fieldMap = {},
    showLoadingSpinner = false,
    disableNameOnEdit = false,
    nameValidationRegex = null,
    showAddIcon = false,
    showReason = false,
    keywordsRequireNumericStart = false,
    useDropdownMode = false,
}) {
    const [name, setName] = useState('');
    const [selectedItems, setSelectedItems] = useState([]);
    const [keywords, setKeywords] = useState([]);
    const [reason, setReason] = useState('');
    const [errors, setErrors] = useState({});
    const [keywordError, setKeywordError] = useState('');
    const [discardChanges, setDiscardChanges] = useState(false);
    const [showDiscardModal, setShowDiscardModal] = useState(false);
    const [dropdownValue, setDropdownValue] = useState('');
    const [fileRules, setFileRules] = useState({});
    const [fileRuleInputs, setFileRuleInputs] = useState({});
    const [fileRuleErrors, setFileRuleErrors] = useState({});

    // Reset form when modal opens or initialData changes
    useEffect(() => {
        if (isOpen) {
            setName(initialData?.[fieldMap.nameReadKey] || '');
            setSelectedItems(initialData?.[fieldMap.itemsReadKey] || []);
            setKeywords(sortAlphabetically(initialData?.[fieldMap.keywordsReadKey]));
            setReason(initialData?.[fieldMap.reasonReadKey] || '');
            setErrors({});
            setKeywordError('');
            setDiscardChanges(false);
            setShowDiscardModal(false);
            setDropdownValue('');
            setFileRules(initialData?.[fieldMap.fileRulesReadKey] || {});
            setFileRuleInputs({});
            setFileRuleErrors({});
        }
    }, [isOpen, initialData]);

    const filteredAvailableItems = (availableItems || []).filter(
        (item) => !selectedItems.includes(item)
    );

    const addFromDropdown = () => {
        if (!dropdownValue) { return; }
        setSelectedItems([...selectedItems, dropdownValue]);
        setDropdownValue('');
        setDiscardChanges(true); // Mark form dirty to trigger discard confirmation on cancel
        if (errors.selectedItems) {
            setErrors((prev) => ({ ...prev, selectedItems: false }));
        }
    };

    const removeSelectedFile = (item) => {
        setSelectedItems((prev) => prev.filter((i) => i !== item));
        setFileRules((prev) => { const next = { ...prev }; delete next[item]; return next; });
        setFileRuleInputs((prev) => { const next = { ...prev }; delete next[item]; return next; });
        setFileRuleErrors((prev) => { const next = { ...prev }; delete next[item]; return next; });
        setDiscardChanges(true); // Mark form dirty to trigger discard confirmation on cancel
    };

    const addRuleToFile = (fileName) => {
        const val = (fileRuleInputs[fileName] || '').trim();
        if (!val) { return; }
        const validationError = validateKeyword(val, keywordsRequireNumericStart);
        if (validationError) {
            setFileRuleErrors((prev) => ({ ...prev, [fileName]: validationError }));
            return;
        }
        if ((fileRules[fileName] || []).includes(val)) {
            setFileRuleErrors((prev) => ({ ...prev, [fileName]: 'Duplicate rule. This rule has already been added.' }));
            return;
        }
        setFileRuleErrors((prev) => ({ ...prev, [fileName]: '' }));
        setFileRules((prev) => ({ ...prev, [fileName]: [...(prev[fileName] || []), val] }));
        setFileRuleInputs((prev) => ({ ...prev, [fileName]: '' }));
        setDiscardChanges(true);
    };

    const removeRuleFromFile = (fileName, rule) => {
        setFileRules((prev) => ({ ...prev, [fileName]: (prev[fileName] || []).filter((r) => r !== rule) }));
        setDiscardChanges(true);
    };

    const handleSave = () => {
        const newErrors = {
            name: !name.trim(),
            selectedItems: selectedItems.length === 0,
        };

        if (showReason) {
            newErrors.reason = !reason.trim();
        }

        setErrors(newErrors);

        if (Object.values(newErrors).some(Boolean)) {
            return;
        }

        const formState = {
            name: name.trim(),
            selectedItems,
            keywords,
            fileRules,
            reason: reason.trim(),
            id: initialData?.id,
        };

        const saveData = fieldMap.buildSaveData
            ? fieldMap.buildSaveData(formState)
            : formState;

        onSave?.(saveData);
    };

    const handleClose = () => {
        if (discardChanges) {
            setShowDiscardModal(true);
        } else {
            onClose?.();
        }
    };

    const handleDiscardConfirm = () => {
        setShowDiscardModal(false);
        setDiscardChanges(false);
        onClose?.();
    };

    const handleDiscardCancel = () => {
        setShowDiscardModal(false);
    };

    const discardModalBody = () => (
        <DiscardChanges
            title={initialData?.id ? 'edit' : 'add'}
            discardClick={handleDiscardConfirm}
            cancelClick={handleDiscardCancel}
        />
    );

    const onDragEnd = (result) => {
        const { source, destination } = result;

        if (!destination) { return; }
        if (source.droppableId === destination.droppableId && source.index === destination.index) { return; }

        setDiscardChanges(true);

        if (source.droppableId === 'available' && destination.droppableId === 'selected') {
            const draggedItem = filteredAvailableItems[source.index];
            if (!draggedItem) { return; }

            setSelectedItems([...selectedItems, draggedItem]);

            if (errors.selectedItems) {
                setErrors((prev) => ({ ...prev, selectedItems: false }));
            }
        } else if (source.droppableId === 'selected' && destination.droppableId === 'available') {
            setSelectedItems(selectedItems.filter((_, index) => index !== source.index));
        } else if (source.droppableId === 'selected' && destination.droppableId === 'selected') {
            const newSelected = Array.from(selectedItems);
            const [removed] = newSelected.splice(source.index, 1);
            if (!removed) { return; }

            newSelected.splice(destination.index, 0, removed);
            setSelectedItems(newSelected);
        }
    };

    if (!isOpen) { return null; }

    // Build the extra field for CIS (Reason input)
    const reasonField = showReason ? (
        <div className={styles.formGroup} style={{ marginBottom: '0' }}>
            <div className={styles.labelHeader}>
                <label className={styles.pluginPanelLabel} style={{ marginBottom: 0 }}>
                    Justification<span className={styles.requiredAsterisk}>*</span>
                </label>
                {errors.reason && <span className={styles.errorText}>Justification is required</span>}
            </div>
            <input
                type="text"
                value={reason}
                maxLength={250}
                onChange={(e) => {
                    const value = e.target.value;
                    if (value.startsWith(' ')) { return; }
                    if (value && !/^[a-zA-Z0-9\s\-_.,]+$/.test(value)) { return; }
                    setReason(value);
                    setDiscardChanges(true);
                    if (errors.reason) {
                        setErrors((prev) => ({ ...prev, reason: false }));
                    }
                }}
                placeholder="Enter Justification"
                className={`${styles.textInput} ${errors.reason ? styles.inputError : ''}`}
            />
        </div>
    ) : null;

    return (
        <>
            <h4 className="schedule-tool">
                {initialData?.id ? (labels.editTitle || 'Edit Configuration') : (labels.createTitle || 'Create Configuration')}
            </h4>

            {showLoadingSpinner && isLoading ? (
                <div style={{ padding: '40px 0' }}>
                    <Spinners />
                </div>
            ) : useDropdownMode ? (
                <>
                    {/* Inline row: Audit Type (left) + Select Audit dropdown + Add (right) */}
                    <div className={styles.inlineRow}>
                        {/* Audit Type field */}
                        <div className={styles.inlineField}>
                            <div className={styles.labelHeader}>
                                <label className={styles.formLabel} style={{ marginBottom: 0 }}>
                                    {labels.nameLabel || 'Name'}<span className={styles.requiredAsterisk}>*</span>
                                </label>
                                {errors.name && <span className={styles.errorText}>{labels.nameError || 'This field is required'}</span>}
                            </div>
                            <input
                                type="text"
                                value={name}
                                maxLength={50}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value.startsWith(' ')) { return; }
                                    if (nameValidationRegex && value && !nameValidationRegex.test(value)) { return; }
                                    setName(value);
                                    setDiscardChanges(true);
                                    if (errors.name) {
                                        setErrors((prev) => ({ ...prev, name: false }));
                                    }
                                }}
                                placeholder={labels.namePlaceholder || 'e.g., Database'}
                                className={`${styles.textInput} ${errors.name ? styles.inputError : ''}`}
                                disabled={disableNameOnEdit && !!initialData?.id}
                            />
                        </div>

                        {/* Select Audit File custom dropdown + Add button */}
                        <div className={styles.inlineField}>
                            <div className={styles.labelHeader}>
                                <label className={styles.formLabel} style={{ marginBottom: 0 }}>
                                    {labels.availableLabel || 'Select File'}<span className={styles.requiredAsterisk}>*</span>
                                </label>
                            </div>
                            <div className={styles.dropdownRow}>
                                <AuditDropdown
                                    items={filteredAvailableItems}
                                    value={dropdownValue}
                                    onChange={setDropdownValue}
                                    placeholder="Select audit file here"
                                />
                                <button
                                    onClick={addFromDropdown}
                                    disabled={!dropdownValue}
                                    className={`${styles.addKeywordButton} ${styles.dropdownAddButton} ${dropdownValue ? styles.addKeywordButtonEnabled : styles.addKeywordButtonDisabled}`}
                                >
                                    Add
                                </button>
                            </div>
                            <div className={styles.helperText}>
                                Select and add audit files one by one
                            </div>
                        </div>
                    </div>

                    {/* Selected Audit File(s) */}
                    <div className={styles.formGroup}>
                        <div className={styles.labelHeader}>
                            <label className={styles.pluginPanelLabel} style={{ marginBottom: 0 }}>
                                {labels.selectedLabel || 'Selected File(s)'}<span className={styles.requiredAsterisk}>*</span>
                            </label>
                            {errors.selectedItems && <span className={styles.errorText}>{labels.selectedError || 'At least one item must be selected'}</span>}
                        </div>
                        <div className={`${styles.selectedFilesContainer} ${errors.selectedItems ? styles.inputError : ''}`}>
                            {selectedItems.length === 0 ? (
                                <p className={styles.emptyMessage}>{labels.selectedEmpty || 'No files selected'}</p>
                            ) : (
                                selectedItems.map((item) => (
                                    <div key={item} className={styles.auditFileCard}>
                                        <div className={styles.auditFileCardHeader}>
                                            <span className={styles.auditFileCardName}>{item}</span>
                                            <button
                                                onClick={() => removeSelectedFile(item)}
                                                className={styles.auditFileCardRemove}
                                                aria-label={`Remove ${item}`}
                                            >
                                                <MdClose className={styles.auditFileCardRemoveIcon} />
                                            </button>
                                        </div>
                                        <div className={styles.rulesSection}>
                                            <div className={styles.labelHeader}>
                                                <label className={styles.rulesLabel}>Rules to Exclude</label>
                                                {fileRuleErrors[item] && <span className={styles.errorText}>{fileRuleErrors[item]}</span>}
                                            </div>
                                            <div className={styles.ruleInputRow}>
                                                <input
                                                    type="text"
                                                    value={fileRuleInputs[item] || ''}
                                                    onChange={(e) => {
                                                        setFileRuleInputs((prev) => ({ ...prev, [item]: e.target.value }));
                                                        if (fileRuleErrors[item]) { setFileRuleErrors((prev) => ({ ...prev, [item]: '' })); }
                                                    }}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRuleToFile(item); } }}
                                                    placeholder="Enter Rule To Exclude"
                                                    className={`${styles.ruleInput} ${fileRuleErrors[item] ? styles.inputError : ''}`}
                                                />
                                                <button
                                                    onClick={() => addRuleToFile(item)}
                                                    disabled={!(fileRuleInputs[item] || '').trim()}
                                                    className={`${styles.addRuleButton} ${(fileRuleInputs[item] || '').trim() ? styles.addRuleButtonEnabled : styles.addRuleButtonDisabled}`}
                                                >
                                                    <MdAdd className={styles.addRuleIcon} />
                                                </button>
                                            </div>
                                            {(fileRules[item] || []).length > 0 && (
                                                <div className={styles.ruleChipsContainer}>
                                                    {(fileRules[item] || []).map((rule) => (
                                                        <div key={rule} className={styles.ruleChip}>
                                                            <span>{rule}</span>
                                                            <button
                                                                onClick={() => removeRuleFromFile(item, rule)}
                                                                className={styles.ruleChipRemove}
                                                                aria-label={`Remove rule ${rule}`}
                                                            >
                                                                <MdClose className={styles.ruleChipRemoveIcon} />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Justification (reason) */}
                    {reasonField}
                </>
            ) : (
                <DragDropContext onDragEnd={onDragEnd}>
                    <div className={styles.formGroup}>
                        <div className={styles.labelHeader}>
                            <label className={styles.formLabel} style={{ marginBottom: 0 }}>
                                {labels.nameLabel || 'Name'}<span className={styles.requiredAsterisk}>*</span>
                            </label>
                            {errors.name && <span className={styles.errorText}>{labels.nameError || 'This field is required'}</span>}
                        </div>
                        <input
                            type="text"
                            value={name}
                            maxLength={50}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value.startsWith(' ')) { return; }
                                if (nameValidationRegex && value && !nameValidationRegex.test(value)) { return; }
                                setName(value);
                                setDiscardChanges(true);
                                if (errors.name) {
                                    setErrors((prev) => ({ ...prev, name: false }));
                                }
                            }}
                            placeholder={labels.namePlaceholder || 'e.g., Database'}
                            className={`${styles.textInput} ${errors.name ? styles.inputError : ''}`}
                            disabled={disableNameOnEdit && !!initialData?.id}
                        />
                    </div>

                    {/* Drag-Drop Panels */}
                    <div>
                        <DragDropPanels
                            availableItems={filteredAvailableItems}
                            selectedItems={selectedItems}
                            onRemoveFromSelected={(item) => {
                                setSelectedItems(selectedItems.filter((i) => i !== item));
                                setDiscardChanges(true);
                            }}
                            labels={labels}
                            errors={errors}
                            extraField={reasonField}
                        />
                        {!showReason && errors.selectedItems && (
                            <span className={styles.errorText}>{labels.selectedError || 'Field should not be empty'}</span>
                        )}
                    </div>

                    {/* Keywords */}
                    <div className={styles.formGroup}>
                        <div className={styles.labelHeader}>
                            <label className={styles.formLabel} style={{ marginBottom: 0 }}>{labels.keywordsLabel || 'Keywords'}</label>
                            {keywordError && <span className={styles.errorText}>{keywordError}</span>}
                        </div>
                        <KeywordInput
                            keywords={keywords}
                            onKeywordsChange={(newKeywords) => {
                                setKeywords(newKeywords);
                                setDiscardChanges(true);
                            }}
                            showAddIcon={showAddIcon}
                            requiresNumericStart={keywordsRequireNumericStart}
                            error={keywordError}
                            onErrorChange={setKeywordError}
                        />
                    </div>
                </DragDropContext>
            )}

            {/* Footer */}
            {!(showLoadingSpinner && isLoading) && (
                <div className="server-btn mt-4 mx-1">
                    <ButtonComponent
                        text="Cancel"
                        className={"cancelBtnStyle btn btn-primary"}
                        data-testid="cancelBtn"
                        onClick={handleClose}
                    />
                    <ButtonComponent
                        text={initialData?.id ? 'Update' : 'Create'}
                        className={"btnStyle btn btn-primary"}
                        data-testid="saveBtn"
                        onClick={handleSave}
                    />
                </div>
            )}

            <CustomModal
                modalOpen={showDiscardModal}
                modalBody={discardModalBody}
                modalHide={handleDiscardCancel}
                closeBtn={true}
                closeBtnFuc={handleDiscardCancel}
            />
        </>
    );
}
